import { useContext } from "react";
import { MessagePrimitive, useMessage } from "@assistant-ui/react";
import {
  useChatMessageById,
  useChatMessages,
  useChatUserEmail,
} from "../../context/ChatMessagesContext";
import { AssistantMarkdown } from "../AssistantMarkdown";
import { ChatFeedbackButtons } from "../ChatFeedbackButtons";
import { AssistantTypeContext } from "./AssistantTypeContext";

/**
 * How a single message renders. Shared by the single-column chat and by each column
 * of a multi-model comparison, so the two never drift apart visually.
 */

export function AssistantUserMessage() {
  return (
    <MessagePrimitive.Root className="flex justify-end mb-3">
      <div className="max-w-[80%] rounded-lg bg-maven-teal-500 text-white px-3 py-2 text-sm">
        <MessagePrimitive.Content />
      </div>
    </MessagePrimitive.Root>
  );
}

export function AssistantAssistantMessage() {
  const message = useMessage((m) => m);
  const chatMessages = useChatMessages();
  const userEmail = useChatUserEmail();
  const assistantType = useContext(AssistantTypeContext);

  // Find the preceding user message (the prompt)
  const messageIndex = chatMessages.findIndex((m) => m.id === message.id);
  const precedingUserMessage = messageIndex > 0
    ? chatMessages.slice(0, messageIndex).reverse().find((m) => m.role === "user")
    : undefined;

  // Get the response text from our ChatMessage (not the @assistant-ui message)
  const chatMessage = chatMessages.find((m) => m.id === message.id);

  return (
    <MessagePrimitive.Root className="flex justify-start mb-3">
      <div className="max-w-[80%]">
        <div className="rounded-lg bg-gray-100 dark:bg-maven-gray-800 text-gray-900 dark:text-maven-gray-100 px-3 py-2 text-sm">
          <MessagePrimitive.Content components={{ Text: AssistantText }} />
        </div>
        {chatMessage && (
          <ChatFeedbackButtons
            messageId={chatMessage.id}
            messageStatus={chatMessage.status}
            prompt={precedingUserMessage?.content || ""}
            response={chatMessage.content}
            userId={userEmail}
            assistantType={assistantType}
            spanId={chatMessage.spanId}
          />
        )}
      </div>
    </MessagePrimitive.Root>
  );
}

function AssistantText({ text }: { text: string }) {
  const messageId = useMessage((m) => m.id);
  const chatMessage = useChatMessageById(messageId);
  const assistantType = useContext(AssistantTypeContext);

  if (!text) {
    return (
      <div className="flex items-center gap-1 py-1">
        <span className="w-1.5 h-1.5 bg-gray-400 dark:bg-maven-gray-300 rounded-full animate-bounce [animation-delay:-0.3s]" />
        <span className="w-1.5 h-1.5 bg-gray-400 dark:bg-maven-gray-300 rounded-full animate-bounce [animation-delay:-0.15s]" />
        <span className="w-1.5 h-1.5 bg-gray-400 dark:bg-maven-gray-300 rounded-full animate-bounce" />
      </div>
    );
  }

  return (
    <AssistantMarkdown
      content={text}
      annotations={chatMessage?.annotations}
      assistantType={assistantType}
    />
  );
}

